import { CalendarResponse } from '../types';
import {
  Schedule,
  ScheduleLegends,
  DateInput,
  Icon,
  Button,
} from '@octano/global-ui';
import { useState, useMemo, useRef } from 'react';
import { Row, Col } from 'reactstrap';
import { useTexts } from '../hooks/useTexts';
import dayjs from 'dayjs';
import ClassroomEventModal, {
  ClassroomEventModalMethods,
} from './ClassroomEventModal';
import { useClassroomCalendar } from './ClassroomLoader';
import { useGetDays } from '../hooks/useGetDays';
import { getDayIdByDate } from '../utils';

type Props = {
  data: CalendarResponse;
  calendarDate?: string | null;
  setCalendarDate: (date: string | null) => void;
};

export const Calendar = ({ data, calendarDate, setCalendarDate }: Props) => {
  const texts = useTexts();
  const [selectedWeek, setSelectedWeek] = useState(
    calendarDate
      ? {
          selectedDay: dayjs(calendarDate),
          start: dayjs(calendarDate).startOf('w'),
          end: dayjs(calendarDate).endOf('w'),
        }
      : {
          selectedDay: dayjs(),
          start: dayjs().startOf('w'),
          end: dayjs().endOf('w'),
        },
  );
  const eventModalRef = useRef<ClassroomEventModalMethods>(null);
  const { refresh } = useClassroomCalendar();

  const days = useGetDays(selectedWeek);

  function nextWeek() {
    setSelectedWeek({
      selectedDay: selectedWeek.selectedDay.add(1, 'w'),
      start: selectedWeek.start.add(1, 'w'),
      end: selectedWeek.end.add(1, 'w'),
    });
  }

  function previousWeek() {
    setSelectedWeek({
      selectedDay: selectedWeek.selectedDay.subtract(1, 'w'),
      start: selectedWeek.start.subtract(1, 'w'),
      end: selectedWeek.end.subtract(1, 'w'),
    });
  }

  function selectWeek(date: string | Date | null) {
    const selectedDate = dayjs(date || undefined);
    setSelectedWeek({
      selectedDay: selectedDate,
      start: selectedDate.startOf('w'),
      end: selectedDate.endOf('w'),
    });
  }

  const schedules = useMemo(
    () =>
      data.classroomCalendar
        .filter(
          (reservation) =>
            !(
              dayjs(reservation.date, 'YYYY-MM-DD').isBefore(
                selectedWeek.start,
              ) ||
              dayjs(reservation.date, 'YYYY-MM-DD').isAfter(selectedWeek.end)
            ),
        )
        .map((reservation) => {
          return {
            scheduleId: reservation.id,
            sectionId: reservation.id,
            sectionName:
              reservation.classroomLesson?.lesson.sectionSchedule.section
                .name || reservation.title,
            day: getDayIdByDate(dayjs(reservation.date)),
            startDate: reservation.date,
            endDate: reservation.date,
            activityId:
              reservation.classroomLesson?.lesson.sectionSchedule.activity.id ||
              data.activities.find((activity) => activity.name === 'Evento')
                ?.id ||
              0,
            moduleStartId: reservation.startModuleId,
            moduleEndId: reservation.endModuleId,
            courseCode: reservation.type,
            tooltip:
              (reservation.event && {
                //Es reserva de un Event
                courseName: reservation.title,
                sectionTitle: '',
                sectionName: '',
                teachers: [''],
                teachersTitle: '',
                classroom: reservation.event.description || '',
              }) ||
              (reservation.classroomLesson && {
                //Es reserva de una Lesson
                courseName:
                  reservation.classroomLesson.lesson.sectionSchedule.section
                    .course.name,
                sectionTitle:
                  reservation.classroomLesson.lesson.sectionSchedule.section
                    .name,
                sectionName: '',
                teachers: reservation.classroomLesson.lesson.sectionSchedule.responsibles.map(
                  (professor) => professor.account.fullName,
                ),
                teachersTitle: texts.teachers,
                classroom: '',
              }) ||
              undefined,
          };
        }),
    [selectedWeek, data, texts.teachers],
  );

  const handleOpenEventModal = () => {
    eventModalRef.current?.open();
  };

  return (
    <>
      <Row className="d-flex align-items-center mx-0 my-2">
        <span onClick={previousWeek} className="cursor-pointer">
          <Icon className="mr-4" name="chevron_left" />
        </span>
        <span onClick={nextWeek} className="cursor-pointer">
          <Icon name="chevron_right" />
        </span>
        <Col
          xs={1}
          className="fs-20 text-uppercase text-primary fw-700 mb-0 ml-4"
        >
          <Row>{`${selectedWeek.start.format('MMMM')}`}</Row>
          <Row>{`${selectedWeek.start.format('YYYY')}`}</Row>
        </Col>
        <Col xs={3}>
          <DateInput
            label={texts.viewCalendar}
            name="name"
            value={selectedWeek.selectedDay.format()}
            onChange={(date) => selectWeek(date)}
          />
        </Col>
        <Button
          className="ml-auto"
          size="md"
          outlined
          text={texts.reserveClassroomButton}
          onClick={handleOpenEventModal}
        />
      </Row>
      <Schedule
        modules={data.modules}
        schedules={schedules}
        mobileNavigation
        days={days}
      />
      <ClassroomEventModal
        ref={eventModalRef}
        classroomId={data.classroom.id}
        modules={data.modules}
        onSave={(eventDate) => {
          if (eventDate) {
            setCalendarDate(eventDate);
          }
          refresh();
        }}
      />
      <ScheduleLegends legends={data.activities} />
    </>
  );
};
